
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { JobHeader } from "@/components/job/JobHeader";
import { JobDescription } from "@/components/job/JobDescription";
import { JobSkeleton } from "@/components/job/JobSkeleton";
import { JobNotFound } from "@/components/job/JobNotFound";
import { JobApplyButton } from "@/components/job/JobApplyButton";
import { JobManagement } from "@/components/job/JobManagement";
import { useJob } from "@/hooks/useJob";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { formatDistanceToNow } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { useEffect } from "react";

export default function Job() {
  const { jobId } = useParams();
  const { data: job, isLoading } = useJob(jobId);
  const { user } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();

  // Check if this is a test invite from sessionStorage
  useEffect(() => {
    const pendingTestId = sessionStorage.getItem('pendingTestId');
    const pendingToken = sessionStorage.getItem('pendingTestInviteToken');

    if (pendingTestId && pendingToken && pendingTestId === jobId) {
      // This is an invited test - show a welcome toast
      toast({
        title: "Test Invitation",
        description: "You've been invited to take this assessment. Review the job details and click 'Take Assessment' when ready.",
        className: "bg-card text-foreground border-none",
      });
      
      // Clear the pending info since we've handled it
      sessionStorage.removeItem('pendingTestId');
      sessionStorage.removeItem('pendingTestInviteToken');
    }
  }, [jobId]);

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <main className="w-full min-h-screen bg-background">
        <div className="container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8">
          <JobSkeleton />
        </div>
      </main>
    );
  }

  if (!job || (job.status !== 'published' && job.organization?.id !== profile?.id)) {
    return (
      <main className="w-full min-h-screen bg-background">
        <div className="container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8">
          <JobNotFound />
        </div>
      </main>
    );
  }

  const isJobCreator = user?.id === job.organization?.id;
  const publishedDate = job.created_at ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true }) : 'Recently';

  return (
    <main className="w-full min-h-screen bg-background">
      <div className="container max-w-7xl mx-auto py-6 px-6 md:px-12 lg:px-24 space-y-6 md:space-y-8">
        <Card className="bg-card border-none rounded-sm">
          <JobHeader
            title={job.title}
            organization={job.organization?.organization}
            fullName={job.organization?.full_name}
            logoUrl={job.organization?.logo_url}
            location={job.location}
            workPresence={job.work_presence}
            workArrangement={job.work_arrangement}
            yearsOfExperience={job.years_of_experience}
            seniority={job.seniority}
            salaryRange={{
              min: job.salary_range_min,
              max: job.salary_range_max,
            }}
            salaryCurrency={job.salary_currency}
            publishedDate={publishedDate}
            applicantCount={job.applicant_count}
            onBack={handleBack}
          />
          <JobDescription
            description={job.description}
            skills={job.key_skills}
          />
          {(!profile || profile.role === 'candidate') && (
            <JobApplyButton 
              jobId={job.id} 
              isActive={job.is_active !== false}
            />
          )}
        </Card>
        
        {isJobCreator && job.organization?.id && (
          <Card className="bg-card border-none rounded-sm p-4">
            <JobManagement 
              jobId={job.id} 
              organizationId={job.organization.id}
              status={job.status} 
            />
          </Card>
        )}
      </div>
    </main>
  );
}
